import { useState, useEffect, useCallback, useMemo } from "react";
import { notification, Button, Space, Tag } from "antd";
import { FormOutlined } from "@ant-design/icons";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import withNavigation from '../hooks/withNavigation.jsx';
import { getModemBaseColumns } from '../../utils/modemCommonColumns.jsx';
import { compareDates, formatDate } from '../../utils/dates';
import { useGetModemTypesLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function ModemUsedList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({ loading: false });

  const [data, setData] = useState({
    raw: [],
    selected: null,
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: modemTypes = EMPTY_ARRAY } = useGetModemTypesLookupQuery();

  // ===== 3. DERIVADOS =====
  const selectedUuid = data.selected?.uuid || "";

  const typeFilter = useMemo(
    () => modemTypes.map(type => ({ text: type.code, value: type.uuid })),
    [modemTypes]
  );

  const deviceFilter = [
    { text: 'Box', value: 'box' },
    { text: 'Router', value: 'router' },
  ];

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    try {
      const res = await service.get("modems", { used: true });

      if (!res || res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res?.data?.message || "Error al obtener datos"),
        });
        updateData('raw', []);
        return;
      }

      updateData('raw', res.data.data || []);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [lang]);


  // ===== 6. HANDLERS =====
  const editModem = () => navigate(`/modems/edit/${selectedUuid}`);

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    Pfetch().then(() => updateUI('loading', false));
  }, [Pfetch]);

  // ===== 8. CONFIG =====
  const onSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selected', selectedRows.length > 0 ? selectedRows[0] : null);
  };

  const columns = [
    ...getModemBaseColumns(lang, typeFilter),
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "device_date",
      render: (value) => formatDate(value) || '-',
      sorter: (a, b) => compareDates(a.device_date, b.device_date),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: lang.handle('Dispositivo'),
      dataIndex: "used_as",
      filters: deviceFilter,
      onFilter: (value, record) => record.used_as === value,
      render: (value) => {
        if (value === 'box') return <Tag color="blue">Box</Tag>;
        if (value === 'router') return <Tag color="purple">Router</Tag>;
        return null;
      },
    },
  ];

  let selection = null;
  if (hasPrivilege('modem_edit')) {
    selection = {
      type: 'radio',
      selectedRowKeys: data.selected?.uuid ? [data.selected.uuid] : [],
      onChange: onSelectionChange,
    };
  }

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        rowSelection={selection}
        columns={columns}
        rowKey="uuid"
        dataSource={data.raw}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('modem_edit') && (
            <Button
              type="primary"
              icon={<FormOutlined />}
              disabled={!data.selected}
              onClick={editModem}
            >
              {lang.handle('Editar')}
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}

export default withLocale(withNavigation(ModemUsedList));
